import { useEffect, useState, useRef } from 'react';
import { toPng } from 'html-to-image';
import { Share2, Download, EyeOff, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ModalCloseButton from './ModalCloseButton';

// Share-card renderer for BOTH the single-stock DRIP tab and the
// portfolio-wide DRIP modal. Renders a branded, fixed-size card that
// is safe to screenshot (no browser chrome, no background tabs, no
// sensitive data from elsewhere on the user's screen).
//
// Mode switches via `variant`:
//   - 'single'    → DripCalcTab (single ticker)
//   - 'portfolio' → PortfolioDripModal (whole account)
//
// `hideDollars` replaces every $ amount with a neutral mask so users
// who want to share on Reddit/X without doxxing their account size
// can still show growth %, timeline, and ticker detail.

function maskDollar() {
  return '$●●●';
}

function fmtDollar(v, hide) {
  if (hide) return maskDollar();
  const n = Number(v) || 0;
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function fmtPct(v) {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return '—';
  return `${Number(v).toFixed(1)}%`;
}

function ShareCard({ variant, payload, hideDollars, cardRef }) {
  const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const isSingle = variant === 'single';

  return (
    <div
      ref={cardRef}
      data-testid="drip-share-card"
      // NOTE: we set an explicit background rather than relying on
      // `bg-card` because html-to-image rasterizes the pixel layer
      // and needs a concrete color for dark-theme captures to render
      // predictably (some theme CSS vars resolve to transparent if
      // the card is cloned off-DOM).
      style={{
        width: '540px',
        background: 'linear-gradient(145deg, #0f172a 0%, #0b1220 60%, #020617 100%)',
        color: '#f8fafc',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      }}
      className="rounded-xl p-6 shadow-2xl"
    >
      {/* Brand header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <img src="/dividrip-icon.png" alt="" width="36" height="36" style={{ width: 36, height: 36 }} />
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>DiviDrip</div>
            <div style={{ fontSize: 10, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {isSingle ? 'DRIP Projection' : 'Portfolio Projection'}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 10, color: '#64748b' }}>{today}</div>
      </div>

      {/* Title block */}
      {isSingle ? (
        <div className="mb-5">
          <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: '-0.02em', color: '#22d3ee' }}>
            {payload.ticker}
          </div>
          {payload.name && (
            <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {payload.name}
            </div>
          )}
        </div>
      ) : (
        <div className="mb-5">
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', color: '#22d3ee' }}>
            My Dividend Portfolio
          </div>
          <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 2 }}>
            DRIP projection · {payload.holdings ?? '—'} {payload.holdings === 1 ? 'holding' : 'holdings'}
          </div>
        </div>
      )}

      {/* Key metrics grid */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        {isSingle ? (
          <>
            <MetricCell label="Portfolio Value" value={fmtDollar(payload.portfolioValue, hideDollars)} accent />
            <MetricCell label="Annual Dividends" value={fmtDollar(payload.annualDividends, hideDollars)} accent />
            <MetricCell label="Total Shares" value={(Number(payload.totalShares) || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })} />
            <MetricCell label="Total Return" value={fmtPct(payload.totalReturn)} accent />
          </>
        ) : (
          <>
            <MetricCell label="Current Value" value={fmtDollar(payload.currentValue, hideDollars)} />
            <MetricCell label={`Value in ${payload.timeline}yr`} value={fmtDollar(payload.futureValue, hideDollars)} accent />
            <MetricCell label="Portfolio Yield" value={fmtPct(payload.portfolioYield)} />
            <MetricCell label={`Div Income / yr (Y${payload.timeline})`} value={fmtDollar(payload.futureIncome, hideDollars)} accent />
          </>
        )}
      </div>

      {/* Assumptions footer */}
      <div
        style={{
          borderTop: '1px solid rgba(148, 163, 184, 0.18)',
          paddingTop: 12,
          fontSize: 10,
          color: '#94a3b8',
          lineHeight: 1.5,
        }}
      >
        {isSingle ? (
          <>
            Monthly: {fmtDollar(payload.monthlyInvest, hideDollars)} · Horizon: {payload.horizon}yr ·
            Initial shares: {payload.initialShares ?? 0} · Price growth: 3%/yr · Div growth: {fmtPct(payload.divGrowth)}
          </>
        ) : (
          <>
            Monthly: {fmtDollar(payload.monthlyContrib, hideDollars)} · Timeline: {payload.timeline}yr ·
            Price growth: {fmtPct(payload.priceGrowth)} · Div growth: {fmtPct(payload.divGrowth)} ·
            Tax: {fmtPct(payload.taxRate)} · DRIP: {payload.drip ? 'ON' : 'OFF'}
          </>
        )}
      </div>

      {/* Brand footer */}
      <div
        style={{
          marginTop: 14,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 10,
        }}
      >
        <span style={{ color: '#64748b' }}>Projection, not financial advice.</span>
        <span style={{ color: '#22d3ee', fontWeight: 700, letterSpacing: '0.02em' }}>twylightcrow.com</span>
      </div>
    </div>
  );
}

function MetricCell({ label, value, accent }) {
  return (
    <div
      style={{
        background: 'rgba(30, 41, 59, 0.55)',
        border: '1px solid rgba(148, 163, 184, 0.15)',
        borderRadius: 10,
        padding: '10px 12px',
      }}
    >
      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', fontWeight: 700 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 800,
          marginTop: 4,
          fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
          color: accent ? '#4ade80' : '#f8fafc',
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </div>
    </div>
  );
}

export default function DripShareModal({ open, onClose, variant, payload }) {
  const [hideDollars, setHideDollars] = useState(false);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);
  // Dynamic preview scaling — see TriangleShareModal for full rationale.
  // The 540px capture target stays untouched (so the PNG is pixel-perfect)
  // but the on-screen preview is scaled to fit whatever width the modal
  // currently has so the card never gets cropped on phone viewports.
  const previewBoxRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(1);
  const [cardNaturalHeight, setCardNaturalHeight] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    const recompute = () => {
      const box = previewBoxRef.current;
      const card = cardRef.current;
      if (!box || !card) return;
      const available = box.clientWidth;
      const scale = Math.min(1, available / 540);
      setPreviewScale(scale > 0 ? scale : 1);
      setCardNaturalHeight(card.scrollHeight);
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    if (previewBoxRef.current) ro.observe(previewBoxRef.current);
    if (cardRef.current) ro.observe(cardRef.current);
    window.addEventListener('resize', recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recompute);
    };
  }, [open, payload, variant, hideDollars]);

  if (!open) return null;

  const filenameBase =
    variant === 'single'
      ? `dividrip-${(payload?.ticker || 'stock').toLowerCase()}-${payload?.horizon || 10}yr`
      : `dividrip-portfolio-${payload?.timeline || 10}yr`;

  // Capture the card to a PNG blob. Called by both Share and Download
  // paths. Uses a 2x pixel ratio so the output is crisp on retina /
  // phone screens when posted to social.
  const capture = async () => {
    if (!cardRef.current) throw new Error('card not mounted');
    const dataUrl = await toPng(cardRef.current, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: '#0b1220',
    });
    // Convert to Blob for Web Share API + download reuse.
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return { dataUrl, blob };
  };

  const handleShare = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { blob, dataUrl } = await capture();
      const file = new File([blob], `${filenameBase}.png`, { type: 'image/png' });
      const canUseShare =
        typeof navigator !== 'undefined' &&
        typeof navigator.share === 'function' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] });

      if (canUseShare) {
        await navigator.share({
          files: [file],
          title: 'DiviDrip projection',
          text:
            variant === 'single'
              ? `My ${payload?.ticker} DRIP projection — ${payload?.horizon}yr`
              : `My dividend portfolio DRIP projection — ${payload?.timeline}yr`,
        });
        toast.success('Shared!');
      } else {
        // Fallback: download the PNG. Android Chrome / desktop browsers
        // without Web Share API + file support land here.
        triggerDownload(dataUrl, `${filenameBase}.png`);
        toast.success('Downloaded — share manually', {
          description: 'Your browser doesn\'t support native share for images. The file is in your Downloads.',
        });
      }
    } catch (err) {
      // AbortError = user dismissed the share sheet. Silent.
      if (err && err.name !== 'AbortError') {
        toast.error('Unable to generate share image', { description: String(err.message || err) });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { dataUrl } = await capture();
      triggerDownload(dataUrl, `${filenameBase}.png`);
      toast.success('Saved to Downloads');
    } catch (err) {
      toast.error('Unable to generate PNG', { description: String(err.message || err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4"
      data-testid="drip-share-modal-overlay"
    >
      <div
        className="bg-card border rounded-xl shadow-2xl w-full max-w-[620px] max-h-[92vh] overflow-y-auto pb-24 sm:pb-4"
        data-testid="drip-share-modal"
      >
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-card z-10">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-primary" />
            <h2 className="text-base font-bold">Share your DRIP projection</h2>
          </div>
          <ModalCloseButton onClick={onClose} testId="drip-share-modal-close" />
        </div>

        <div className="p-4 space-y-4">
          <p className="text-xs text-muted-foreground">
            Only the card shown below is captured — nothing else on your screen.
          </p>

          {/* Hide dollars toggle */}
          <button
            type="button"
            onClick={() => setHideDollars(v => !v)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold transition-colors ${
              hideDollars
                ? 'bg-primary/10 border-primary/40 text-primary'
                : 'bg-muted border-border text-foreground hover:bg-muted/70'
            }`}
            data-testid="drip-share-toggle-hide-dollars"
            aria-pressed={hideDollars}
          >
            {hideDollars ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {hideDollars ? 'Dollar amounts hidden' : 'Hide dollar amounts'}
          </button>
          {hideDollars && (
            <p className="text-[11px] text-muted-foreground -mt-2 pl-1">
              Dollar figures are masked on the shared card. Percentages, ticker, and timeline are still visible.
            </p>
          )}

          {/* Preview — rendered at true 540px capture size; the live
              preview is scaled by ResizeObserver to fit whatever width
              the modal currently has, so the card never gets cropped on
              phone viewports. The PNG download stays full-fidelity. */}
          <div
            ref={previewBoxRef}
            className="w-full rounded-lg border bg-muted/30 p-3 overflow-hidden flex justify-center"
            style={{ height: cardNaturalHeight > 0 ? cardNaturalHeight * previewScale + 24 : undefined }}
          >
            <div
              style={{
                transformOrigin: 'top center',
                transform: `scale(${previewScale})`,
                width: 540,
                flex: '0 0 540px',
              }}
            >
              <ShareCard variant={variant} payload={payload} hideDollars={hideDollars} cardRef={cardRef} />
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={handleShare}
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-60"
              data-testid="drip-share-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
              Share
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-lg border bg-background font-semibold text-sm hover:bg-muted transition-colors disabled:opacity-60"
              data-testid="drip-share-download-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download PNG
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function triggerDownload(dataUrl, filename) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
